import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';

import Icon from '../../../components/AppIcon';
import { formService } from '../../../services/formService';
import { notificationService } from '../../../services/notificationService';

const UserInquiries = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [liveUpdateIndicator, setLiveUpdateIndicator] = useState(false);

  useEffect(() => {
    loadInquiries();
    setupNotifications();

    // Subscribe to real-time new submissions
    const submissionsChannel = notificationService?.subscribeToFormSubmissions((newSubmission) => {
      // Add new submission to list
      setInquiries(prev => [newSubmission, ...prev]);

      // Show live update indicator
      setLiveUpdateIndicator(true);
      setTimeout(() => setLiveUpdateIndicator(false), 3000);

      // Show browser notification
      if (notificationsEnabled) {
        const notificationData = notificationService?.formatNotificationMessage(newSubmission);
        notificationService?.showNotification(notificationData?.title, {
          body: notificationData?.body,
          tag: notificationData?.tag,
          requireInteraction: true
        });
      }
    });

    // Subscribe to real-time status updates
    const updatesChannel = notificationService?.subscribeToFormUpdates((updatedSubmission) => {
      // Update existing submission in list
      setInquiries(prev => 
        prev?.map(inquiry => 
          inquiry?.id === updatedSubmission?.id ? updatedSubmission : inquiry
        )
      );

      // Update selected inquiry if it's the one being updated
      if (selectedInquiry?.id === updatedSubmission?.id) {
        setSelectedInquiry(updatedSubmission);
      }

      // Show live update indicator
      setLiveUpdateIndicator(true);
      setTimeout(() => setLiveUpdateIndicator(false), 3000);
    });

    // Cleanup subscriptions
    return () => {
      notificationService?.unsubscribe(submissionsChannel);
      notificationService?.unsubscribe(updatesChannel);
    };
  }, [notificationsEnabled, selectedInquiry?.id]);

  const setupNotifications = async () => {
    const granted = await notificationService?.requestPermission();
    setNotificationsEnabled(granted);
  };

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const data = await formService?.getAllSubmissions();
      setInquiries(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (inquiryId, newStatus) => {
    try {
      await formService?.updateSubmissionStatus(inquiryId, newStatus);
      // Real-time subscription will handle the update
    } catch (err) {
      setError(err?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;

    try {
      await formService?.deleteSubmission(inquiryId);
      // Remove from local state
      setInquiries(prev => prev?.filter(inq => inq?.id !== inquiryId));
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(null);
      }
    } catch (err) {
      setError(err?.message || 'Failed to delete inquiry');
    }
  };

  const statusFilters = [
    { id: 'all', label: 'All', count: inquiries?.length || 0 },
    { id: 'new', label: 'New', count: inquiries?.filter(i => i?.status === 'new')?.length || 0 },
    { id: 'replied', label: 'Replied', count: inquiries?.filter(i => i?.status === 'replied')?.length || 0 },
    { id: 'archived', label: 'Archived', count: inquiries?.filter(i => i?.status === 'archived')?.length || 0 }
  ];

  const getStatusColor = (status) => {
    const colors = {
      new: 'primary',
      replied: 'success',
      archived: 'muted'
    };
    return colors?.[status] || 'muted';
  };

  const getTypeIcon = (type) => {
    const icons = {
      consultation: 'Calendar',
      partnership: 'Handshake',
      contact: 'MessageCircle'
    };
    return icons?.[type] || 'Mail';
  };

  const filteredInquiries = filterStatus === 'all'
    ? inquiries
    : inquiries?.filter(inq => inq?.status === filterStatus);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading inquiries...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}
      {/* Live Update Indicator */}
      {liveUpdateIndicator && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg"
        >
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-green-500 font-medium">Live update received</span>
        </motion.div>
      )}
      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        {statusFilters?.map((filter) => (
          <Button
            key={filter?.id}
            variant={filterStatus === filter?.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus(filter?.id)}
            className="touch-manipulation"
          >
            {filter?.label}
            <span className="ml-2 px-2 py-0.5 rounded-full bg-background/20 text-xs">
              {filter?.count}
            </span>
          </Button>
        ))}
      </div>
      {/* Inquiries List */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* List View */}
        <div className="space-y-4">
          {filteredInquiries?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" />
              <p className="text-muted-foreground mt-4">No inquiries found</p>
            </div>
          ) : (
            filteredInquiries?.map((inquiry) => (
              <motion.div
                key={inquiry?.id}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className={`rounded-xl p-4 sm:p-6 border cursor-pointer transition-all touch-manipulation ${
                  selectedInquiry?.id === inquiry?.id
                    ? 'border-primary bg-primary/10' :'border-border/50 hover:border-primary/50'
                }`}
                style={{
                  background: selectedInquiry?.id === inquiry?.id
                    ? 'linear-gradient(135deg, rgba(20, 184, 166, 0.15), rgba(15, 23, 42, 0.9))'
                    : 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8))'
                }}
                onClick={() => setSelectedInquiry(inquiry)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: `linear-gradient(135deg, var(--color-${getStatusColor(inquiry?.status)}), var(--color-secondary))`
                      }}
                    >
                      <Icon name={getTypeIcon(inquiry?.form_type)} size={18} color="white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-foreground font-semibold truncate">{inquiry?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{inquiry?.email}</p>
                    </div>
                  </div>
                  <span
                    className="px-2 sm:px-3 py-1 rounded-full text-xs font-medium flex-shrink-0"
                    style={{
                      background: `var(--color-${getStatusColor(inquiry?.status)})`,
                      color: 'white'
                    }}
                  >
                    {inquiry?.status}
                  </span>
                </div>

                <h3 className="text-foreground font-medium mb-2 text-sm sm:text-base">{inquiry?.subject || inquiry?.form_type}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-3">
                  {inquiry?.message}
                </p>
                <p className="text-xs text-muted-foreground">{new Date(inquiry?.created_at)?.toLocaleDateString()}</p>
              </motion.div>
            ))
          )}
        </div>

        {/* Detail View */}
        <div
          className={`rounded-xl p-4 sm:p-6 border border-border/50 backdrop-blur-xl ${
            selectedInquiry ? 'block' : 'hidden xl:block'
          } xl:sticky xl:top-24`}
          style={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8))'
          }}
        >
          {selectedInquiry ? (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-1">
                    {selectedInquiry?.name}
                  </h2>
                  <p className="text-muted-foreground">{selectedInquiry?.email}</p>
                  {selectedInquiry?.phone && (
                    <p className="text-muted-foreground text-sm">{selectedInquiry?.phone}</p>
                  )}
                </div>
                <Button variant="ghost" size="icon" iconName="X" onClick={() => setSelectedInquiry(null)} />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Subject</label>
                  <p className="text-foreground font-medium mt-1">{selectedInquiry?.subject || selectedInquiry?.form_type}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Message</label>
                  <p className="text-foreground mt-1 leading-relaxed">{selectedInquiry?.message}</p>
                </div>

                <div className="flex items-center gap-4 text-sm">
                  <div>
                    <label className="text-muted-foreground">Type:</label>
                    <span className="ml-2 text-foreground capitalize">{selectedInquiry?.form_type}</span>
                  </div>
                  <div>
                    <label className="text-muted-foreground">Date:</label>
                    <span className="ml-2 text-foreground">{new Date(selectedInquiry?.created_at)?.toLocaleDateString()}</span>
                  </div>
                </div>

                {selectedInquiry?.metadata && Object.keys(selectedInquiry?.metadata)?.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Additional Info</label>
                    <div className="mt-2 space-y-1">
                      {Object.entries(selectedInquiry?.metadata)?.map(([key, value]) => (
                        <p key={key} className="text-sm text-foreground">
                          <span className="text-muted-foreground capitalize">{key?.replace(/([A-Z])/g, ' $1')}:</span> {value}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button 
                    variant="default" 
                    iconName="Check"
                    onClick={() => handleStatusUpdate(selectedInquiry?.id, 'replied')}
                    disabled={selectedInquiry?.status === 'replied'}
                  >
                    Mark Replied
                  </Button>
                  <Button 
                    variant="outline" 
                    iconName="Archive"
                    onClick={() => handleStatusUpdate(selectedInquiry?.id, 'archived')}
                  >
                    Archive
                  </Button>
                  <Button 
                    variant="destructive" 
                    iconName="Trash2"
                    onClick={() => handleDelete(selectedInquiry?.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
                style={{
                  background: 'linear-gradient(135deg, var(--color-muted), var(--color-background))'
                }}
              >
                <Icon name="MessageSquare" size={40} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-muted-foreground">
                Select an inquiry to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInquiries;